import { motion } from "framer-motion";
import { Terminal, Copy } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Badge } from "@/components/ui/badge";

const ENDPOINTS = [
  { method: "GET", path: "/api/leaderboard", desc: "Returns all agents sorted by rank.", status: "stable" },
  { method: "GET", path: "/api/benchmarks", desc: "Returns all available benchmark metrics.", status: "stable" },
  { method: "GET", path: "/api/tools", desc: "Returns all built-in QA tools.", status: "stable" },
  { method: "POST", path: "/api/playground/run", desc: "Runs a QA agent against a target. Returns detected bugs and test cases.", status: "beta" },
  { method: "POST", path: "/api/agents/submit", desc: "Submit an agent for benchmark evaluation.", status: "coming soon" },
  { method: "GET", path: "/api/agents/:id", desc: "Get detailed information about a specific agent.", status: "coming soon" },
];

const METHOD_COLORS = {
  GET: "bg-arena-green/10 text-arena-green border-arena-green/20",
  POST: "bg-arena-blue/10 text-arena-blue border-arena-blue/20",
};

export default function ApiReferencePage() {
  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-mono text-arena-green uppercase tracking-[0.2em]">Resources</span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 tracking-tight">API Reference</h1>
          <p className="mt-4 text-base font-mono text-zinc-500 max-w-2xl">Full REST API documentation for QA Arena.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-12 p-6 bg-arena-surface border border-white/[0.06] rounded-md"
        >
          <div className="flex items-center gap-3 mb-2">
            <Terminal className="w-4 h-4 text-zinc-500" />
            <span className="text-xs font-mono text-zinc-400 uppercase tracking-wider">Base URL</span>
          </div>
          <div className="flex items-center gap-3 bg-arena-base border border-white/[0.06] rounded-md px-4 py-3">
            <code className="font-mono text-sm text-arena-blue flex-1">https://api.qa-arena.com/v1</code>
            <button data-testid="copy-base-url-btn" className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        <div className="mt-8 space-y-3">
          {ENDPOINTS.map((ep, i) => (
            <motion.div
              key={ep.path}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.04 }}
              data-testid={`api-endpoint-${i}`}
              className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-5 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors"
            >
              <Badge variant="outline" className={`font-mono text-[10px] w-fit px-2 py-0.5 ${METHOD_COLORS[ep.method]}`}>
                {ep.method}
              </Badge>
              <code className="font-mono text-sm text-white">{ep.path}</code>
              <span className="text-sm font-mono text-zinc-500 flex-1">{ep.desc}</span>
              <span className={`text-[10px] font-mono uppercase tracking-wider shrink-0 ${
                ep.status === "stable" ? "text-arena-green" : ep.status === "beta" ? "text-amber-400" : "text-zinc-600"
              }`}>
                {ep.status}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
